import { lighten, Typography, useTheme } from '@material-ui/core';
import * as React from 'react';
import { useEffect } from 'react';
import { BarChart } from '../../../../components/muiLegacy/Charts/BarChart';
import AnalysisChartLoading from '../../AnalysisChartLoading';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../AnalysisList';
import { internationalText } from '../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../DataTimedOut';
import NotEnoughData from '../NotEnoughData';
import NotEnoughTagGroups from '../NotEnoughTagGroups';

export interface TopicalDemandProps extends ChartProps {}

const TopicalDemand: React.FC<TopicalDemandProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: { showSerpOnly: true, showTagGroupIds: true },
      })),
    []
  );

  // Fetch data
  const topicVolumeQuery = projectsApiService.queries.analysis.useGetTopicalDemand({
    projectId: project.id,
    serpOnly: analysisFilterState?.serpOnly,
    tagGroupIds: analysisFilterState?.tagGroupIds,
  });

  const topicVolumeLoading = topicVolumeQuery?.query?.loading;
  const topicVolumeData = topicVolumeQuery?.result?.tagGroupAnalyses;

  const sortedData = topicVolumeData?.slice().sort(function (a, b) {
    return b.searchVolume - a.searchVolume;
  });

  // Styling
  const theme = useTheme();

  // Render
  if (didQueryTimeout(topicVolumeQuery?.query)) {
    return <DataTimedOut />;
  }

  if (topicVolumeLoading) {
    return <AnalysisChartLoading />;
  }

  if (!analysisFilterState?.tagGroupIds?.length) {
    return <NotEnoughTagGroups />;
  }

  if (!topicVolumeData.length) {
    return <NotEnoughData />;
  }

  return (
    <BarChart
      leftLegend={internationalText.chart.demand_by_topic.volume[language]}
      palette={palette}
      ResponsiveBarProps={{
        data: sortedData,
        keys: ['searchVolume'],
        indexBy: 'tagGroupId',
        colors: palette.tertiaryScale[1],
        borderRadius: 5,
        borderWidth: 0,
        axisBottom: {
          format: (v: any) => topicVolumeData.find((x) => x.tagGroupId === v)?.tagGroupName,
          tickSize: 0,
          tickPadding: 10,
          tickRotation: 0,
          legend: internationalText.chart.demand_by_topic.topics[language],
          legendPosition: 'middle',
          legendOffset: theme.spacing(6),
        },
      }}
    />
  );
};

export default TopicalDemand;
